/**
 * ActionHistory `changes` 필드 매핑 — 이 파일이 정본이다 (백엔드 문서는 2026-09-14 에 폐기).
 * 백엔드 모델 필드가 바뀌면 여기를 갱신한다.
 *
 * - 모델별 필드 키 → 한국어 라벨
 * - 코드값 → 라벨 (role, achieve_type, link_type)
 * - 매핑되지 않은 키는 호출 측에서 원본 키를 그대로 표시
 */

// 공통 필드 (모든 BaseModel 상속 모델)
const COMMON_FIELD_LABELS: Record<string, string> = {
  id: 'ID',
  created_at: '생성일시',
  deleted_at: '삭제일시',
  visible: '표시여부',
}

// 모델별 필드 라벨
const MODEL_FIELD_LABELS: Record<string, Record<string, string>> = {
  account: {
    last_login: '마지막 로그인',
    name: '이름',
    email: '이메일',
    phone: '전화번호',
    is_active: '활성 여부',
    is_staff: '스태프 여부',
    is_superuser: '관리자 여부',
  },
  org: {
    name: '기관명',
    logo_path: '로고 경로',
    academy: '연결 대학',
    meta: '기관 설정',
  },
  org_member: {
    org: '기관',
    account: '계정',
    role: '역할',
    available: '사용 가능 여부',
  },
  org_member_workspace: {
    available: '사용 가능 여부',
    org_member: '기관 멤버',
    workspace: '워크스페이스',
    sort_key: '순서',
  },
  workspace: {
    available: '사용 가능 여부',
    org: '기관',
    name: '워크스페이스명',
    sort_key: '정렬 순서',
  },
  business: {
    workspace: '워크스페이스',
    name: '사업명',
    sort_key: '정렬 순서',
    available: '사용 가능 여부',
    report_settings: '보고서 설정',
  },
  business_label: {
    business: '사업',
    key: '키',
  },
  business_detail_field: {
    business: '사업',
    key: '필드 키',
    name: '필드명',
  },
  business_extra_field: {
    business: '사업',
    key: '필드 키',
    name: '필드명',
  },
  department: {
    available: '사용 가능 여부',
    workspace: '워크스페이스',
    name: '부서명',
    sort_key: '정렬 순서',
  },
  file: {
    workspace: '워크스페이스',
    target_type: '대상 타입',
    target_id: '대상 ID',
    file_path: '파일 경로',
    file_name: '파일명',
    file_extension: '확장자',
  },
  article: {
    workspace: '워크스페이스',
    creator: '생성자',
    assignee: '담당자',
    title: '제목',
    category: '게시글 타입',
    content: '내용',
  },
  task: {
    name: '과제명',
    origin_workspace: '원본 워크스페이스',
    department: '부서',
    assignee: '담당자',
    sub_assignee: '부담당자',
    manager: '관리자',
    reviewer: '검토자',
    labels: '라벨 ID 목록',
  },
  task_detail_info: {
    task: '과제',
    business_detail_field: '사업 상세 필드',
    value: '값',
  },
  task_extra_info: {
    task: '과제',
    business_extra_field: '사업 추가 필드',
    value: '값',
  },
  task_map: {
    task_sub_group: '실행과제',
    task: '세부실행과제',
    origin: '원본 여부',
    overwrite_name: '재정의 과제명',
    sort_key: '정렬 순서',
  },
  task_comment: {
    task: '세부실행과제',
    workspace: '워크스페이스',
    creator: '작성자',
    content: '댓글 내용',
  },
  task_item: {
    task: '세부실행과제',
    name: '수행업무명',
    start_at: '시작일시',
    end_at: '종료일시',
    output: '산출물',
    sort_key: '정렬 순서',
    reviewer_approved: '검토자 승인 여부',
    reviewer_approved_at: '검토자 승인 일시',
    reviewer_comment: '검토자 코멘트',
    manager_approved: '관리자 승인 여부',
    manager_approved_at: '관리자 승인 일시',
    manager_comment: '관리자 코멘트',
    assignee_approved: '담당자 승인 여부',
    assignee_approved_at: '담당자 승인 일시',
    assignee_comment: '담당자 코멘트',
  },
  task_report: {
    task: '세부실행과제',
    report_template_key: '보고서 템플릿 키',
    status: '상태',
    grade: '평가 등급',
  },
  task_report_info: {
    task_report: '세부실행과제 보고서',
    template_field_key: '보고서 템플릿 필드 키',
    value: '값',
  },
  task_budget: {
    task: '세부실행과제',
    result_description: '결과 설명',
  },
  task_budget_item: {
    task_budget: '세부실행과제 예산',
    name: '예산항목명',
    target_figure: '목표금액',
    done_figure: '완료금액',
  },
  task_group: {
    workspace: '워크스페이스',
    business: '사업',
    name: '전략과제명',
    sort_key: '정렬 순서',
  },
  task_sub_group: {
    task_group: '전략과제',
    name: '실행과제명',
    sort_key: '정렬 순서',
  },
  task_performance: {
    task: '세부실행과제',
    name: '성과명',
    unit: '단위',
  },
  task_performance_item: {
    task_performance: '세부실행과제 성과',
    name: '성과항목명',
    achieve_type: '달성 방식',
    target_figure: '목표수치',
    done_figure: '달성수치',
    manual_achieved: '수동 달성 여부',
  },
  performance_group: {
    workspace: '워크스페이스',
    business: '사업',
    name: '지표구분명',
    sort_key: '정렬 키',
  },
  performance_category: {
    group: '지표구분',
    parent: '상위 분류',
    name: '분류명',
    manual_weight: '수동 가중치',
    sort_key: '정렬 키',
  },
  performance_class: {
    performance_category: '성과 분류',
    name: '성과지표명',
    target_figure_formula: '목표수치 공식',
    done_figure_formula: '달성수치 공식',
    manual_weight: '수동 가중치',
    sort_key: '정렬 키',
  },
  performance_map: {
    performance_class: '성과지표',
    link_type: '연결 타입',
    target_id: '대상 ID',
    sort_key: '정렬 키',
    formular_key: '공식 키',
  },
  performance: {
    assignee: '담당자',
    department: '부서',
    name: '성과명',
    unit: '단위',
  },
  performance_item: {
    performance: '성과',
    name: '성과항목명',
    achieve_type: '달성 방식',
    target_figure: '목표수치',
    done_figure: '달성수치',
    manual_achieved: '수동 달성 여부',
  },
}

// 코드값 → 라벨
const CODE_VALUE_LABELS: Record<string, Record<string, string>> = {
  role: {
    owner: '소유자',
    admin: '관리자',
    normal: '일반',
  },
  achieve_type: {
    figure: '수치 기반',
    manual: '수동 설정',
  },
  link_type: {
    direct: '직접 연결',
    task: '과제 연결',
  },
}

/**
 * 모델 키 → 한국어 표시명 (변경 대상 컬럼용)
 */
const MODEL_LABELS: Record<string, string> = {
  account: '계정',
  org: '기관',
  org_member: '기관 멤버',
  org_member_workspace: '멤버 워크스페이스',
  workspace: '워크스페이스',
  business: '사업',
  business_label: '사업 라벨',
  business_detail_field: '사업 상세 필드',
  business_extra_field: '사업 추가 필드',
  department: '부서',
  file: '파일',
  article: '게시글',
  task: '세부실행과제',
  task_detail_info: '세부실행과제 상세 정보',
  task_extra_info: '세부실행과제 추가 정보',
  task_map: '과제 맵',
  task_comment: '세부실행과제 댓글',
  task_item: '수행업무',
  task_report: '보고서',
  task_report_info: '보고서 정보',
  task_budget: '예산',
  task_budget_item: '예산 항목',
  task_group: '전략과제',
  task_sub_group: '실행과제',
  task_performance: '성과',
  task_performance_item: '성과 항목',
  performance_group: '지표구분',
  performance_category: '성과 분류',
  performance_class: '성과지표',
  performance_map: '성과 맵',
  performance: '성과',
  performance_item: '성과 항목',
  role: '역할',
  achieve_type: '달성 유형',
  link_type: '연결 유형',
}

/**
 * 모델 키로 한국어 표시명을 조회. 매핑이 없으면 원본 값(또는 '-')을 반환.
 */
export function getModelLabel(model: string | null | undefined): string {
  if (!model) return '-'
  return MODEL_LABELS[model] ?? model
}

/**
 * 모델 + 필드 키로 한국어 라벨을 조회. 매핑이 없으면 null 반환.
 */
export function getFieldLabel(
  model: string | null | undefined,
  key: string
): string | null {
  if (model) {
    const modelMap = MODEL_FIELD_LABELS[model]
    if (modelMap && modelMap[key]) return modelMap[key]
  }
  if (COMMON_FIELD_LABELS[key]) return COMMON_FIELD_LABELS[key]
  return null
}

/**
 * 코드값 필드(role, achieve_type, link_type)인 경우 라벨로 변환.
 * 매핑이 없으면 원본 값을 그대로 반환.
 */
export function formatCodeValue(key: string, value: unknown): unknown {
  if (typeof value !== 'string') return value
  const codeMap = CODE_VALUE_LABELS[key]
  if (codeMap && codeMap[value]) return codeMap[value]
  return value
}
